import type { AxiosInstance } from "axios";
import type {
  CheckUserIdResponse,
  SignupRequest,
  SignupResponse,
  LoginRequest,
  LoginResponse,
  UserProfile,
} from "@growing/types";

// Re-export types from shared package
export type {
  CheckUserIdResponse,
  SignupRequest,
  SignupResponse,
  LoginRequest,
  LoginResponse,
  UserProfile,
} from "@growing/types";

export interface AuthApiConfig {
  /** 사용자 역할 (로그인/로그아웃 경로 결정에 사용) */
  role: "student" | "teacher" | "admin";
}

/**
 * Auth API 함수들을 생성합니다.
 * role에 따라 로그인 시 expectedRole, 로그아웃/탈퇴 경로가 결정됩니다.
 */
export function createAuthApi(client: AxiosInstance, config: AuthApiConfig) {
  const { role } = config;

  // teacher와 admin은 같은 경로 사용
  const rolePath = role === "student" ? "student" : "teacher";
  const shouldAddExpectedRole = role !== "student";

  return {
    /**
     * 아이디 중복 확인
     */
    getCheckUserId: async (userId: string): Promise<CheckUserIdResponse> => {
      const response = await client.get<CheckUserIdResponse>(
        `/auth/check-userid/${userId}`
      );
      return response.data;
    },

    /**
     * 회원가입
     */
    postSignup: async (data: SignupRequest): Promise<SignupResponse> => {
      const response = await client.post<SignupResponse>("/auth/signup", data);
      return response.data;
    },

    /**
     * 로그인
     * teacher/admin인 경우 expectedRole을 추가하여 학생 계정 로그인 차단
     */
    postLogin: async (data: LoginRequest): Promise<LoginResponse> => {
      const loginData = shouldAddExpectedRole
        ? { ...data, expectedRole: "teacher" as const }
        : data;
      const response = await client.post<LoginResponse>("/auth/login", loginData);
      return response.data;
    },

    /**
     * 로그아웃
     */
    postLogout: async (): Promise<void> => {
      await client.post(`/auth/logout/${rolePath}`);
    },

    /**
     * 내 프로필 조회
     */
    getMyProfile: async (): Promise<UserProfile> => {
      const response = await client.get<UserProfile>("/auth/me");
      return response.data;
    },

    /**
     * 회원 탈퇴
     */
    deleteAccount: async (): Promise<void> => {
      await client.delete(`/auth/account/${rolePath}`);
    },

    /**
     * 프로필 정보 수정
     */
    updateProfile: async (data: {
      phoneNumber?: string;
      school?: string;
    }): Promise<UserProfile> => {
      const response = await client.patch<UserProfile>("/auth/profile", data);
      return response.data;
    },

    /**
     * 비밀번호 변경
     */
    updatePassword: async (data: {
      currentPassword: string;
      newPassword: string;
    }): Promise<void> => {
      await client.patch("/auth/password", data);
    },

    /**
     * 전화번호 변경 (SMS 인증 포함)
     */
    updatePhoneNumber: async (data: {
      newPhoneNumber: string;
      verificationCode: string;
    }): Promise<UserProfile> => {
      const response = await client.patch<UserProfile>(
        "/auth/phone-number",
        data
      );
      return response.data;
    },
  };
}
